import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DISPATCHER_PREFIX} from '../../../consts/data-service-consts';
import {FieldUiDataObj, FieldUiDynamicListObj, KeyValueObj, UiDataObj} from '../../../../generated/generated-core.model';
import {Observable, of, Subject} from 'rxjs';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MxFormErrorMessageService} from './mx-form-error-message.service';
import {distinctUntilChanged, startWith, takeUntil, tap} from 'rxjs/operators';
import {MxFormMode} from './mx-form-fields-builder.service';

export class TtlMap<K, V> {
    private readonly ttl: number;
    private timesCache: Map<K, number> = new Map<K, number>();
    private variables: Map<K, V> = new Map<K, V>();

    constructor(ttlSeconds: number) {
        this.ttl = ttlSeconds * 1000;
    }

    public set(key: K, value: V): void {
        this.timesCache.set(key, new Date().getTime());
        this.variables.set(key, value);
    }

    public get(key: K): V | undefined {
        let time = this.timesCache.get(key);
        if (!time) {
            return;
        }
        if (time + this.ttl < new Date().getTime()) {
            this.variables.delete(key);
            return;
        } else {
            return this.variables.get(key);
        }
    }
}


@Injectable({
    providedIn: 'root'
})
export class MxDynamicFormService {
    private uiDataRestUrl: string = DISPATCHER_PREFIX + '/ui-data';
    private cache: TtlMap<string, UiDataObj> = new TtlMap<string, UiDataObj>(60);

    constructor(private httpClient: HttpClient, private errorService: MxFormErrorMessageService) {
    }

    getUIData(className: string, useCache: boolean = false, force: boolean = false): Observable<UiDataObj> {
        let mxUiDataObj: UiDataObj;
        if (useCache) {
            mxUiDataObj = this.cache.get(className);
        }
        if (mxUiDataObj) {
            return of(mxUiDataObj);
        }
        return this.httpClient.post<UiDataObj>(`${this.uiDataRestUrl}${force ? '/force' : ''}`, className).pipe(
            tap(object => this.cache.set(className, object)));
    }

    getUiDynamicCombo(listNameValue: FieldUiDynamicListObj): Observable<KeyValueObj[]> {
        return this.httpClient.post<KeyValueObj[]>(this.uiDataRestUrl + '/dynamic-list', listNameValue);
    }

    private addFieldsToForm(fields: Array<FieldUiDataObj>, form: FormGroup, formMode: MxFormMode, destroy$$: Subject<null>): void {
        for (let field of fields) {
            let validators = [];
            let requiredValidator = this.errorService.requiredValidator(field.validation.required, field.validation.type);
            if (requiredValidator) {
                validators.push(requiredValidator);
            }
            let maxLengthValidator = this.errorService.maxLengthValidator(field.validation.maxLength, field.validation.type);
            if (maxLengthValidator) {
                validators.push(maxLengthValidator);
            }
            let minLengthValidator = this.errorService.minLengthValidator(field.validation.minLength, field.validation.type);
            if (minLengthValidator) {
                validators.push(minLengthValidator);
            }
            if (field.validation.pattern) {
                validators.push(this.errorService.boPatternValidator(field.validation.pattern));
            }
            if (field.validation.defaultValue) {
                validators.push(this.errorService.defaultValueValidator(field.validation.defaultValue));
            }
            if (field.validation.type) {
                validators.push(this.errorService.boValidationValidator(field.validation.type, field.name));
            }
            if (typeof field.validation.minValue === 'number') {
                validators.push(Validators.min(field.validation.minValue));
            }
            if (typeof field.validation.maxValue === 'number') {
                validators.push(Validators.max(field.validation.maxValue));
            }

            if (typeof field.validation.integerDigits === 'number' && typeof field.validation.fractionDigits === 'number') {
                validators.push(this.errorService.boDigitValidator(field.validation.integerDigits, field.validation.fractionDigits));
            }

            let fieldDisabled = field.disabledOnEdit && formMode === 'edit';

            if (field.type !== 'group') {
                let formControl = new FormControl(null, validators);
                if (fieldDisabled || field.disabled) {
                    formControl.disable();
                }
                if (field.disabledDependsOnField) {
                    formControl.disable();
                    let dependOnControl = form.get([field.disabledDependsOnField]);
                    dependOnControl.valueChanges.pipe(
                        takeUntil(destroy$$),
                        distinctUntilChanged()
                    )
                        .subscribe(value => {
                            if (value && !field.disabled) {
                                formControl.enable();
                            } else {
                                formControl.setValue(null);
                                formControl.disable();
                            }
                        });
                }
                if (field.showIf) {
                    let [showIfField, showIfValue] = field.showIf.split('=');
                    if (showIfValue !== undefined && showIfField !== undefined) {
                        form.get([showIfField]).valueChanges.pipe(
                            takeUntil(destroy$$),
                            startWith(form.get([showIfField]).value),
                            distinctUntilChanged()
                        ).subscribe(value => {
                            if (value ===  showIfValue) {
                                formControl.enable();
                            } else {
                                formControl.disable();
                                formControl.reset();
                            }
                        });
                    }
                }
                form.addControl(field.name, formControl);
            } else if (field.type === 'group' && field.showChildDependOn) {
                let dependOnControl = form.get([field.showChildDependOn]);
                let setValue = (newValue) => {
                    let groupToDelete = field.sublist.reduce(
                        (acc, x) => acc.concat(x.sublist.map(item => {
                            return {name: item.name, type: item.type};
                        })), []);
                    if (groupToDelete) {
                        groupToDelete.forEach(value => {
                            if (value.type === 'password') {
                                let passwordCtrl = form.get(value.name);
                                let retypePasswordCtrl = form.get(value.name + ' retype');
                                if (passwordCtrl && retypePasswordCtrl) {
                                    passwordCtrl.setValidators([]);
                                    retypePasswordCtrl.setValidators([]);
                                }
                                form.removeControl(value.name);
                                form.removeControl(value.name + ' retype');
                            } else {
                                form.removeControl(value.name);
                            }
                        });
                    }
                    let groupToCreate = field.sublist.find(value => value.name === newValue);
                    if (groupToCreate) {
                        this.addFieldsToForm(groupToCreate.sublist, form, formMode, destroy$$);
                    }
                };
                setValue(dependOnControl.value);
                if (formMode !== 'readonly') {
                    dependOnControl.valueChanges
                        .pipe(
                            takeUntil(destroy$$)
                        ).subscribe(setValue);
                }
            }

            if (field.sublist) {
                this.addFieldsToForm(field.sublist, form, formMode, destroy$$);
            }
        }
    }

    buildForm(fields: Array<FieldUiDataObj>, formMode: MxFormMode, destroy$$: Subject<null>): FormGroup {
        let form: FormGroup = new FormGroup({});
        this.addFieldsToForm(fields, form, formMode, destroy$$);
        return form;
    }

    fillForm(form: FormGroup, fields: Array<FieldUiDataObj>, values: any): void {
        if (form && form.controls) {
            Object.entries(form.controls).forEach(([i, control]: [string, FormControl]) => {
                if (values && values[i] !== undefined) {
                    if (values[i] !== null) {
                        control.setValue(values[i]);
                    } else {
                        control.reset();
                    }
                } else if (fields) {
                    let foundDefaultValue = this.findDefaultValue(i, fields);
                    if (foundDefaultValue !== undefined) {
                        control.setValue(foundDefaultValue);
                    }
                }
                if (fields && !control.disabled) {
                    let fieldMeta = fields.find(item => item.name === i);

                    if (fieldMeta &&
                        fieldMeta.disabledOnEditValues &&
                        fieldMeta.disabledOnEditValues
                            .findIndex(item => item == control.value) > -1) {
                        control.disable();
                    }
                }
            });
        }
    }

    private findDefaultValue(fieldName: string, fields: Array<FieldUiDataObj>): any {
        for (let value of fields) {
            if (value.name === fieldName) {
                return value.defaultValue;
            } else if (value.sublist) {
                let found = this.findDefaultValue(fieldName, value.sublist);
                if (found !== undefined) {
                    return found;
                }
            }
        }
        return undefined;
    }
}
